import ExploreHighlightBlock from "@components/ExploreHighlightBlock"
import ExploreHighlightBlockSmallCards from "@components/ExploreHighlightBlock/SmallCards"
import { getIn } from "@utils/index"
import { getExploreEntryURL } from "@utils/urls"
import { graphql, useStaticQuery } from "gatsby"
import * as R from "ramda"
import React from "react"

import { ContentfulContentBaseProps } from "../types"

interface ContentfulExploreHighlightBlockProps
  extends ContentfulContentBaseProps {}

const ContentfulExploreHighlightBlock: React.FC<
  ContentfulExploreHighlightBlockProps
> = ({
  blockId,
  entryType,
  link,
  linkText,
  showTags,
  smallCardsOnly,
  subtitle,
  title,
  titleColumnSize,
}) => {
  const data = useStaticQuery(graphql`
    query ContentfulExploreHighlightBlockEntries {
      fieldLogs: allContentfulExploreEntry(
        filter: { entryType: { slug: { eq: "field-logs" } } }
        sort: { createdAt: DESC }
        limit: 4
      ) {
        edges {
          node {
            ...ExploreListEntryFields
          }
        }
      }
      ambassadors: allContentfulExploreEntry(
        filter: { entryType: { slug: { eq: "ambassadors" } } }
        sort: { createdAt: DESC }
        limit: 4
      ) {
        edges {
          node {
            ...ExploreListEntryFields
          }
        }
      }
    }
  `)

  const entriesByTypeSlug = {
    ambassadors: getIn(data, "ambassadors"),
    "field-logs": getIn(data, "fieldLogs"),
  }

  const blockType = getIn(entryType, "slug")
  const blockEntries = getIn(entriesByTypeSlug, `${blockType}.edges`, [])

  if (R.isEmpty(blockEntries)) {
    return null
  }

  const baseProps = {
    actionLink: link,
    actionText: linkText,
    subtitle,
    title,
    titleColSize: titleColumnSize,
  }

  const formatCards = (card: any) => ({
    image: getIn(card, "node.cardImage.file.url"),
    link: getExploreEntryURL(blockType, getIn(card, "node.slug")),
    tag: getIn(card, "node.tag"),
    title: getIn(card, "node.title"),
  })

  const featuredCard = getIn(blockEntries, "0.node")

  return smallCardsOnly ? (
    <ExploreHighlightBlockSmallCards
      {...baseProps}
      blockId={blockId}
      cards={R.map(formatCards)(blockEntries)}
    />
  ) : (
    <ExploreHighlightBlock
      {...baseProps}
      blockId={blockId}
      cards={
        blockEntries.length > 1
          ? R.map(formatCards)(R.takeLast(3, blockEntries))
          : []
      }
      featuredCard={{
        actionLink: getExploreEntryURL(blockType, getIn(featuredCard, "slug")),
        actionText: "Check it Out",
        description: getIn(featuredCard, "description.description"),
        highlightLabel: getIn(featuredCard, "tagLabel"),
        highlightText: getIn(featuredCard, "tag"),
        image: getIn(featuredCard, "largeCardImage.file.url"),
        showTags: showTags,
        title: getIn(featuredCard, "title"),
      }}
      showTags={showTags}
    />
  )
}

export default ContentfulExploreHighlightBlock
